import {
  ArrowRight,
  Building2,
  DollarSign,
  FileText,
  HelpCircle,
  Users
} from 'lucide-react';
import type { Contact } from '@/lib/services/contacts/types';
import type {
  PropertyInstruction,
  PropertyTransferSubFlow,
  BeneficiaryHoldingType,
  AssetOwnershipType
} from '@/lib/services/redemption';

// =============================================================================
// Constants
// =============================================================================

export const INSTRUCTION_LABELS: Record<PropertyInstruction, string> = {
  sell: 'Sell',
  'transfer-to-beneficiary': 'Transfer to Beneficiary',
  'notice-of-death': 'Notice of Death',
  undecided: 'Undecided'
};

export const INSTRUCTION_ICONS: Record<PropertyInstruction, typeof DollarSign> =
  {
    sell: DollarSign,
    'transfer-to-beneficiary': Users,
    'notice-of-death': FileText,
    undecided: HelpCircle
  };

export const OWNERSHIP_OPTIONS = [
  { value: 'solely-held', label: 'Solely Held' },
  { value: 'tenants-in-common', label: 'Tenants in Common' },
  { value: 'joint-tenants', label: 'Joint Tenants' }
] as const;

export const OWNERSHIP_LABELS: Record<string, string> = Object.fromEntries(
  OWNERSHIP_OPTIONS.map((o) => [o.value, o.label])
);

export const SUB_FLOW_LABELS: Record<PropertyTransferSubFlow, string> = {
  'direct-to-beneficiary': 'Direct to Beneficiary',
  'via-executor': 'Via Executor'
};

export const SUB_FLOW_ICONS = {
  'direct-to-beneficiary': ArrowRight,
  'via-executor': Building2
} as const;

export const DIRECT_TRANSFER_STATES = ['NSW'];

// =============================================================================
// Chrome
// =============================================================================

/*
  Moved to `../instruction-chrome` when the *share* instruction section needed the
  same four functions (LF-6314 follow-up). Re-exported here so this module's existing
  importers are unchanged, and so there is exactly one definition of each — a second
  copy is what had already drifted by a border shade the first time these were
  inlined.
*/
export {
  instructionCardClass,
  instructionChoiceClass,
  instructionLabelClass,
  instructionPanelClass
} from '../instruction-chrome';

// =============================================================================
// Helper functions
// =============================================================================

export function getAvailableInstructions(
  ownershipType: string | null | undefined
): PropertyInstruction[] {
  if (!ownershipType) return [];
  switch (ownershipType) {
    case 'solely-held':
    case 'tenants-in-common':
      return ['sell', 'transfer-to-beneficiary', 'undecided'];
    case 'joint-tenants':
      return ['notice-of-death'];
    default:
      return [];
  }
}

// =============================================================================
// Types
// =============================================================================

export interface TransferBeneficiary {
  contactId: number;
  contact?: { id: number; firstName: string; lastName: string };
  percentage?: number;
}

export interface PropertyInstructionSectionProps {
  mode: 'read' | 'edit';
  /**
   * Which chrome to wear (LF-6314). Defaults to `form` — the look the real-estate
   * form has today — so adding this moved no shipped pixel. See
   * {@link instructionLabelClass}.
   */
  variant?: 'form' | 'compact';
  ownershipType: string | null | undefined;
  instruction: PropertyInstruction | null | undefined;
  milestoneSummary?: string;
  transferBeneficiaries?: TransferBeneficiary[];
  transferSubFlow?: PropertyTransferSubFlow | null;
  hasContraryIntention?: boolean;
  beneficiaryHoldingType?: BeneficiaryHoldingType | null;
  propertyState?: string | null;
  percentageOwned?: number | null;
  hasLinkedMortgage?: boolean;
  contacts?: Contact[];
  /**
   * **One call, because one user action must be one write** (VALE-355).
   *
   * Choosing an ownership type used to fan out into up to three `PUT`s — the type,
   * a clear of the now-invalid instruction, and (for joint tenants) the auto-set of
   * `notice-of-death`. They raced: each request reads `itemInfo`, merges its own key
   * and saves the whole blob, so the later ones wrote back a snapshot taken before
   * the first had committed and **silently reverted the ownership type**. Switching
   * away from Joint Tenants therefore did nothing at all.
   *
   * `instruction` is the third argument for exactly that reason: `undefined` means
   * *keep what is there* (the current one is still valid under the new type), `null`
   * means *clear it* (it is not, and there is more than one to choose from), and a
   * value means *this is the only one the new type allows*. The host folds all of it
   * into a single patch.
   */
  onOwnershipTypeChange?: (
    ownershipType: AssetOwnershipType | null,
    isNonEstate: boolean,
    instruction?: PropertyInstruction | null
  ) => void | Promise<unknown>;
  onInstructionChange?: (instruction: PropertyInstruction | null) => void;
  onTransferSubFlowChange?: (subFlow: PropertyTransferSubFlow) => void;
  /**
   * **Returns the commit when the host has one** (VALE-355). The drawer's handler is
   * a real `PUT` and the add dialog's writes to a buffer; typed `void`, the promise
   * was discarded and the compact branch's optimistic mask lifted a tick after the
   * click instead of when the write landed — which is the flicker this widening
   * exists to remove. A host that returns nothing is still valid.
   */
  onTransferBeneficiariesChange?: (
    beneficiaries: TransferBeneficiary[]
  ) => void | Promise<unknown>;
  onBeneficiaryHoldingTypeChange?: (
    type: BeneficiaryHoldingType | null
  ) => void;
  onContraryIntentionChange?: (value: boolean) => void;
  onPercentageOwnedChange?: (value: number | null) => void;
}
